import { CAPABILITY_RELEASE_PILOT_ORG_IDS } from './constants';

/**
 * The Capability release (FE #647): Capability Setup, the Capability and
 * Activity tabs, the Activity tier names (Low to Exceptional, for Rust to
 * Platinum), the header without Top 3, and the Members tier as an average.
 *
 * It is set per org while it is piloted, by the org's `flags.capabilityRelease`
 * (BE, set with scripts/setOrgFlag.js) or the pilot list in constants.ts:
 * - `true` (or the pilot list): on for everyone.
 * - `'admin'`: on for the people who set it up (org admins, team managers,
 *   platform admins) and off for everyone else, so admins can build teams,
 *   roles and levels before any member sees a thing. Members keep the app as
 *   it was, and nothing that reaches them (the "Role in <team>" question,
 *   assessments, practice nudges) runs; the BE holds the same line.
 * - anything else: off, the app as it was before the release. The screens it
 *   rewrote keep their earlier versions alongside (the `…Legacy` files).
 *
 * Components read it with useCapabilityRelease(). Plain functions and data
 * (tier names, help copy, the Members sort) read isCapabilityRelease(), which
 * AppRoutes keeps in step with the signed-in user on every render.
 *
 * Once it is on for every org: delete this file, the hook, the pilot list and
 * the `…Legacy` files, and keep the released branch of each check.
 */
type OrgFlagsLike = { authId?: string; flags?: { capabilityRelease?: boolean | 'admin' } } | null | undefined;

export type CapabilityReleaseMode = 'on' | 'admin' | 'off';

export const capabilityReleaseMode = (org: OrgFlagsLike): CapabilityReleaseMode => {
  if (org?.flags?.capabilityRelease === true) return 'on';
  if (org?.authId && CAPABILITY_RELEASE_PILOT_ORG_IDS.includes(org.authId)) return 'on';
  if (org?.flags?.capabilityRelease === 'admin') return 'admin';
  return 'off';
};

/** Whether this viewer sees the release: everyone when it is on, only admins in admin mode. */
export const capabilityReleaseFor = (org: OrgFlagsLike, viewerIsAdmin = false): boolean => {
  const mode = capabilityReleaseMode(org);
  return mode === 'on' || (mode === 'admin' && viewerIsAdmin);
};

let released = false;

/** For AppRoutes only: the signed-in user's answer, mirrored for code outside components. */
export const setCapabilityRelease = (on: boolean): void => {
  released = on;
};

export const isCapabilityRelease = (): boolean => released;
